'use client';

import LinkifyIt from 'linkify-it';
import { Calendar, ExternalLink, Link as LinkIcon, MapPin } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { ProfileIdentityBadges } from '@/components/shared/ProfileIdentityBadges';
import { convertUserEntitiesToTwitterEntities } from '@/components/shared/previews/normalize-twitter-entities';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { asRoute } from '@/lib/routes/app';
import { expandUrlsInText, getProfileUrls } from '@/lib/url-utils-client';
import { cn, formatNumber } from '@/lib/utils';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import type { FollowState, PreviewMetrics } from './types';

const PROTOCOL_PREFIX_PATTERN = /^https?:\/\//;

export interface PreviewWrapperProperties {
  children: ReactNode;
  profileId?: string | null;
  username?: string | null;
}

interface UserPreviewCardProperties {
  bannerReady: boolean;
  error: boolean;
  followState: FollowState;
  formatJoinedAt: (date: Date) => string;
  loading: boolean;
  locationIsLinkable: boolean;
  metrics: PreviewMetrics;
  renderAffiliationPreview: (properties: PreviewWrapperProperties) => ReactNode;
  showFallbackLoading: boolean;
  twitterProfileUrl: string | null;
  user: UserPreviewVM | null;
}

export function UserPreviewCard({
  user,
  loading,
  error,
  showFallbackLoading,
  bannerReady,
  twitterProfileUrl,
  followState,
  metrics,
  locationIsLinkable,
  renderAffiliationPreview,
  formatJoinedAt,
}: UserPreviewCardProperties) {
  const { isSelf, isFollowing, isFollowLoading, followButtonText, toggleFollow } = followState;

  const renderLoadingSkeleton = (withStatus: boolean) => (
    <div className="max-h-[600px] overflow-y-auto">
      <div className="space-y-3 px-4 py-4">
        <div className="flex items-start gap-3">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-12 w-full" />
        <div className="flex gap-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
        </div>
        {withStatus ? <div className="text-xs text-muted-foreground">Still loading profile...</div> : null}
      </div>
    </div>
  );

  if (showFallbackLoading) {
    return renderLoadingSkeleton(true);
  }

  if (error) {
    return (
      <div className="space-y-2 px-4 py-4">
        <div className="text-sm text-muted-foreground">Unable to load user preview</div>
        {twitterProfileUrl ? (
          <Button size="sm" variant="outline" asChild className="w-full">
            <a href={twitterProfileUrl} target="_blank" rel="noopener noreferrer">
              View on Twitter
            </a>
          </Button>
        ) : null}
      </div>
    );
  }

  if (loading && !user) {
    return renderLoadingSkeleton(false);
  }

  if (!user) {
    return null;
  }

  const userRoute = asRoute(`/u/${user.username}`);
  const { followingCount, followersCount, tweetCount, interactionsCount } = metrics;
  const normalizedEntities = convertUserEntitiesToTwitterEntities(user.entities);
  const previewMetricsProps: PreviewMetrics = {};
  if (typeof followingCount === 'number') {
    previewMetricsProps.followingCount = followingCount;
  }
  if (typeof followersCount === 'number') {
    previewMetricsProps.followersCount = followersCount;
  }
  if (typeof tweetCount === 'number') {
    previewMetricsProps.tweetCount = tweetCount;
  }
  if (typeof interactionsCount === 'number') {
    previewMetricsProps.interactionsCount = interactionsCount;
  }

  return (
    <div className="max-h-[600px] overflow-y-auto">
      <div className="pb-4">
        <div className="relative h-24 w-full bg-muted">
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-r from-muted/60 via-muted to-muted/80',
              user.profileBannerUrl ? 'transition-opacity duration-200' : undefined,
              user.profileBannerUrl && bannerReady ? 'opacity-0' : 'opacity-100'
            )}
            aria-hidden
          />
          {user.profileBannerUrl ? (
            <Image
              src={user.profileBannerUrl}
              alt=""
              fill
              sizes="384px"
              className={cn('object-cover transition-opacity duration-200', bannerReady ? 'opacity-100' : 'opacity-0')}
              priority={false}
              unoptimized
            />
          ) : null}
          <div className="absolute left-4 bottom-0 translate-y-1/2 sm:left-5">
            <Link href={userRoute} prefetch={false} aria-label={`Analyze @${user.username}`}>
              <Avatar
                src={user.profileImageUrl || undefined}
                alt={user.name}
                size="xl"
                className="ring-4 ring-card shadow-lg transition-transform hover:scale-105"
              />
            </Link>
          </div>
          <div className="absolute right-4 top-4 flex items-center gap-2 sm:right-5">
            <UserPreviewQuickLinks
              username={user.username}
              isSelf={isSelf}
              isFollowing={isFollowing}
              isFollowLoading={isFollowLoading}
              followButtonText={followButtonText}
              toggleFollow={toggleFollow}
            />
          </div>
        </div>
        <div className="px-4 pb-1 pt-0.5 sm:px-5 sm:pb-1.5 sm:pt-2">
          <div className="space-y-1.5">
            <div className="min-w-0 pl-24 sm:pl-28">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Link
                    href={userRoute}
                    prefetch={false}
                    aria-label={`Analyze @${user.username}`}
                    className="max-w-full truncate text-lg font-semibold leading-tight hover:underline"
                  >
                    {user.name}
                  </Link>
                  <ProfileIdentityBadges
                    verifiedType={user.verifiedType}
                    affiliations={user.affiliations}
                    size="sm"
                    renderAffiliation={({ username: affiliationUsername, affiliation, content }): ReactNode => {
                      const organizationProfileId = affiliation?.organizationProfileId ?? null;
                      if (organizationProfileId || affiliationUsername) {
                        return renderAffiliationPreview({
                          profileId: organizationProfileId,
                          username: affiliationUsername ?? null,
                          children: content,
                        });
                      }

                      return content;
                    }}
                  />
                </div>
                <Link
                  href={userRoute}
                  prefetch={false}
                  aria-label={`Analyze @${user.username}`}
                  className="text-sm text-muted-foreground hover:underline"
                >
                  @{user.username}
                </Link>
              </div>
            </div>

            {user.description ? (
              <div>
                <DescriptionWithLinks text={user.description} entities={normalizedEntities} />
              </div>
            ) : null}

            <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
              {renderLocation(user.location, locationIsLinkable)}
              {renderWebsite(user.url, normalizedEntities)}
              {user.createdAt ? (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Joined {formatJoinedAt(user.createdAt)}
                </span>
              ) : null}
            </div>

            <UserPreviewStats {...previewMetricsProps} />
          </div>
        </div>
      </div>
    </div>
  );
}

type NormalizedUserEntities = ReturnType<typeof convertUserEntitiesToTwitterEntities>;

function renderLocation(location: string | null | undefined, linkable: boolean) {
  if (!location) return null;
  if (linkable) {
    return (
      <Link
        href={`/explore?location=${encodeURIComponent(location)}`}
        prefetch={false}
        className="flex items-center gap-1 transition-colors hover:text-foreground"
        aria-label={`Explore profiles near ${location}`}
      >
        <MapPin className="h-3 w-3" />
        <span className="underline decoration-dotted underline-offset-2">{location}</span>
      </Link>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <MapPin className="h-3 w-3" />
      <span>{location}</span>
    </span>
  );
}

function renderWebsite(url: string | null | undefined, entities: NormalizedUserEntities) {
  if (!url) return null;
  const urlInfo = getProfileUrls(url, entities);
  if (!urlInfo) return null;
  return (
    <a
      href={urlInfo.expanded}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1 text-foreground transition-colors hover:text-primary"
    >
      <LinkIcon className="h-3 w-3" />
      {urlInfo.display}
    </a>
  );
}

interface UserPreviewQuickLinksProperties extends FollowState {
  username: string;
}

function UserPreviewQuickLinks({
  username,
  isSelf,
  isFollowing,
  isFollowLoading,
  followButtonText,
  toggleFollow,
}: UserPreviewQuickLinksProperties) {
  return (
    <div className="flex items-center gap-2">
      {isSelf ? null : (
        <Button
          size="sm"
          variant={isFollowing ? 'outline' : 'default'}
          onClick={toggleFollow}
          disabled={isFollowLoading}
          className="h-8 px-3 text-xs font-medium"
        >
          {isFollowLoading ? (
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            followButtonText
          )}
        </Button>
      )}
      <Button size="sm" variant="outline" asChild className="h-8 px-2 border-border/60 hover:border-border">
        <a
          href={`https://twitter.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open @${username} on Twitter`}
        >
          <ExternalLink className="h-3 w-3" />
          <span className="sr-only">View profile on X</span>
        </a>
      </Button>
    </div>
  );
}

type UserPreviewStatsProperties = PreviewMetrics;

function UserPreviewStats({
  followingCount,
  followersCount,
  tweetCount,
  interactionsCount,
}: UserPreviewStatsProperties) {
  const entries: Array<{ label: string; value: number | undefined }> = [
    { label: 'Following', value: followingCount },
    { label: 'Followers', value: followersCount },
    { label: 'Tweets', value: tweetCount },
    { label: 'Interactions', value: interactionsCount },
  ];

  const hasStats = entries.some((entry) => typeof entry.value === 'number');
  if (!hasStats) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-wrap gap-5 border-t border-border/60 pt-2 pb-1.5 text-sm">
      {entries.map(({ label, value }) => (
        <div key={label} className="flex min-w-[4rem] flex-col">
          <span className="font-semibold">{typeof value === 'number' ? formatNumber(value) : '—'}</span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
      ))}
    </div>
  );
}

function DescriptionWithLinks({ text, entities }: { text: string; entities: NormalizedUserEntities }) {
  const linkify = new LinkifyIt();
  const expanded = expandUrlsInText(text, entities);
  const matches = linkify.match(expanded) || [];
  if (matches.length === 0) {
    return <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{expanded}</p>;
  }
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of matches) {
    if (match.index > last) nodes.push(expanded.slice(last, match.index));
    const display = match.url.replace(PROTOCOL_PREFIX_PATTERN, '');
    nodes.push(
      <a
        key={`${match.index}-${match.lastIndex}`}
        href={match.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline"
      >
        {display}
      </a>
    );
    last = match.lastIndex;
  }
  if (last < expanded.length) nodes.push(expanded.slice(last));
  return <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{nodes}</p>;
}
